import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Seo from "@/components/feature/Seo";
import TopicCard from "@/components/feature/TopicCard";
import ReadingProgress from "./components/ReadingProgress";
import CommentSection from "./components/CommentSection";
import { topics } from "@/mocks/topics";
import { categories, siteMeta } from "@/mocks/site";
import { formatCount } from "@/utils/format";
import type { Topic } from "@/types/content";

const allTopics = topics as Topic[];

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = allTopics.find((item) => item.id === id);

  if (!topic) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Seo
          title="话题不存在 · 爆料91"
          description="你访问的话题可能已被合并或调整，返回热议话题列表查看当前正在讨论的内容。"
          path="/topics"
        />
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-24">
          <div className="text-center max-w-md">
            <span className="mx-auto w-16 h-16 rounded-full bg-background-100 text-foreground-500 flex items-center justify-center">
              <i className="ri-emotion-unhappy-line text-3xl"></i>
            </span>
            <h1 className="mt-6 text-2xl font-black text-foreground-950 font-heading">
              这个话题暂时找不到了
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              它可能已经被合并进其他话题，或者链接有误。回到热议话题列表，看看当前大家都在聊什么。
            </p>
            <Link
              to="/topics"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回热议话题
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = categories.find((item) => item.id === topic.category);
  const related = allTopics
    .filter((item) => item.id !== topic.id && item.category === topic.category)
    .slice(0, 4);
  const fallbackRelated = allTopics.filter((item) => item.id !== topic.id).slice(0, 4);
  const relatedList = related.length > 0 ? related : fallbackRelated;
  const detailUrl = `https://gaohaoshuzi.cn/topic/${topic.id}`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: topic.title,
      description: topic.summary,
      image: topic.cover,
      datePublished: topic.date,
      dateModified: topic.date,
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: topic.author },
      publisher: { "@type": "Organization", name: siteMeta.name },
      mainEntityOfPage: { "@type": "WebPage", "@id": detailUrl },
      keywords: topic.tags.join(","),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://gaohaoshuzi.cn/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "热议话题",
          item: "https://gaohaoshuzi.cn/topics",
        },
        { "@type": "ListItem", position: 3, name: topic.title, item: detailUrl },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Seo
        title={`${topic.title} · 爆料91热议话题`}
        description={`${topic.summary.slice(0, 70)}… 来爆料91看这个话题下的评论区热门讨论与舆论反应。`}
        keywords={`${topic.title},爆料91热议话题,爆料91评论区,爆料91网友讨论`}
        path={`/topic/${topic.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <ReadingProgress />

      <main className="flex-1">
        <section className="w-full px-4 md:px-8 lg:px-12 pt-6 pb-10 bg-background-100">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "热议话题", to: "/topics" },
              { label: topic.title },
            ]}
          />

          <div className="mt-6 max-w-3xl">
            <Link
              to={`/topics?category=${topic.category}`}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold hover:bg-primary-200 transition-colors"
            >
              <i className={category?.icon ?? "ri-fire-line"}></i>
              {category?.name ?? "热议话题"}
            </Link>

            <h1 className="mt-4 text-2xl md:text-4xl font-black text-foreground-950 font-heading leading-snug">
              {topic.title}
            </h1>

            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
              {topic.summary}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-3-line"></i>
                {topic.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {topic.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                约 {topic.readTime}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {formatCount(topic.views)} 浏览
              </span>
              <a
                href="#comments"
                className="inline-flex items-center gap-1.5 hover:text-primary-600 transition-colors"
              >
                <i className="ri-chat-3-line"></i>
                {topic.commentCount} 条讨论
              </a>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-10">
            <article className="min-w-0">
              <div className="w-full aspect-[16/9] rounded-3xl overflow-hidden bg-background-200">
                <img
                  src={topic.cover}
                  alt={`${topic.title} 话题配图`}
                  title={`${topic.title} - 爆料91热议话题`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-8 flex flex-col gap-8">
                {topic.sections.map((section) => (
                  <section key={section.heading}>
                    <h2 className="text-lg md:text-xl font-black text-foreground-950 font-heading flex items-start gap-2.5">
                      <span className="mt-1.5 w-1.5 h-5 rounded-full bg-primary-500 shrink-0"></span>
                      {section.heading}
                    </h2>
                    <div className="mt-3 flex flex-col gap-3">
                      {section.paragraphs.map((paragraph, index) => (
                        <p
                          key={index}
                          className="text-sm md:text-base leading-[1.9] text-foreground-700"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <blockquote className="mt-8 rounded-2xl bg-accent-50 border-l-4 border-accent-500 px-5 py-5">
                <p className="text-sm md:text-base font-medium leading-relaxed text-foreground-800">
                  {topic.quote}
                </p>
                <footer className="mt-2 text-xs text-foreground-500">
                  —— 评论区高频观点整理
                </footer>
              </blockquote>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-foreground-700">相关标签：</span>
                {topic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topics?keyword=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs font-medium hover:bg-secondary-200 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-8">
                <CommentSection key={topic.id} topicId={topic.id} initialCount={topic.commentCount} />
              </div>
            </article>

            <aside className="flex flex-col gap-5 lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-2xl bg-background-100 border border-background-200 p-5">
                <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                  <span className="w-6 h-6 rounded-md bg-secondary-500 text-background-50 flex items-center justify-center">
                    <i className="ri-links-line text-sm"></i>
                  </span>
                  相关推荐
                </h2>
                <div className="mt-3">
                  {relatedList.map((item) => (
                    <TopicCard key={item.id} topic={item} variant="compact" />
                  ))}
                </div>
                <Link
                  to="/topics"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  查看更多话题
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="rounded-2xl bg-primary-950 text-background-100 p-5">
                <h2 className="text-base font-bold text-background-50">你的观点也很重要</h2>
                <p className="mt-2 text-sm leading-relaxed text-background-200/80">
                  看到不同立场别急着划走，回到评论区说说你的判断，理性表达更容易被接茬。
                </p>
                <a
                  href="#comments"
                  className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-accent-500 text-primary-950 text-sm font-black hover:bg-accent-400 transition-colors whitespace-nowrap"
                >
                  <i className="ri-chat-3-fill"></i>
                  去评论区
                </a>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}